import {inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {Anliegen, Subanliegen} from "../core/model/anliegen";
import {AnliegenService} from "../core/services/apiservices/anliegen.service";
import {Nachricht} from "../core/model/nachricht/nachricht";
import {AnliegenMap} from "../core/model/anliegen-map";
import {NachrichtenTypeEnum} from "../core/model/nachricht/nachrichten-type.enum";

export interface AnliegenInfo {
    anliegenName?: string;
    subanliegenName?: string;
    anhangErlaubt?: boolean;
}

@Injectable({providedIn: 'root'})
export class AnliegenHelperService {

    private readonly anliegenService = inject(AnliegenService);

    /**
     * Liefert die Namen von Anliegen und Subanliegen basierend auf der Nachricht.
     */
    getAnliegenInfo(nachricht: Nachricht): Observable<AnliegenInfo> {
        return this.anliegenService
            .getAnliegen(nachricht.typ, nachricht.dienststellennummer)
            .pipe(
                map((anliegenList) => {
                    const parent = this.findAnliegenBySubanliegenBezug(anliegenList ?? [], nachricht.bezug);
                    const subanliegen = parent ? this.findSubanliegenByBezug(parent, nachricht.bezug) : undefined;
                    return parent
                        ? {
                            anliegenName: parent.name,
                            subanliegenName: subanliegen?.name,
                            anhangErlaubt: subanliegen?.anhangErlaubt ?? false,
                        }
                        : {};
                })
            );
    }

    public getAnliegenWithGroupName(
        subanliegenId: string,
        anliegenListe: Anliegen[]
    ): { gruppenName: string; subanliegen: Subanliegen } | null {
        for (const anliegen of anliegenListe ?? []) {
            const subanliegen = anliegen.subanliegen?.find((s) => String(s.id) === String(subanliegenId));
            if (subanliegen) return {gruppenName: anliegen.name, subanliegen};
        }
        return null;
    }

    public getAnliegenWithTypeAndGroupName(
        subanliegenId: string,
        anliegenMap: AnliegenMap
    ): { typ: NachrichtenTypeEnum; gruppenName: string; subanliegen: Subanliegen } | null {
        for (const [typ, anliegenListe] of Object.entries(anliegenMap ?? {}) as [NachrichtenTypeEnum, Anliegen[]][]) {
            const result = this.getAnliegenWithGroupName(subanliegenId, anliegenListe ?? []);
            if (result) return {typ, ...result};
        }
        return null;
    }

    private findAnliegenBySubanliegenBezug(anliegenList: Anliegen[], bezug: string): Anliegen | undefined {
        return (anliegenList ?? []).find((a) => a.subanliegen?.some((s) => s.bezug === bezug));
    }

    private findSubanliegenByBezug(anliegen: Anliegen, bezug: string): Subanliegen | undefined {
        return anliegen.subanliegen?.find((s) => s.bezug === bezug);
    }
}